import React, { useState, useEffect } from 'react';
import { Upload, Button, Form, Input, Select, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import axios from 'axios';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';

const { Dragger } = Upload;
const { Option } = Select;

interface Category {
  celebrationCategoryId: string;
  name: string;
}

interface AddEcardFormProps {
  onUploadComplete?: () => void;
}

const AddEcardForm: React.FC<AddEcardFormProps> = ({ onUploadComplete }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [form] = Form.useForm();
  const [images, setImages] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const { instance, accounts } = useMsal();

  // Fetch authentication token
  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = await fetchToken();
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/CelebrationCategories/ActiveWithIds`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        message.error('Failed to fetch celebration categories');
      }
    };

    fetchCategories();
  }, []);

  // Initialize token on component mount
  useEffect(() => {
    fetchToken();
  }, []);

  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (images.length === 0) {
      message.error('Please upload at least one image.');
      return;
    }

    setLoading(true);

    try {
      for (const image of images) {
        const formData = new FormData();
        formData.append('EcardName', values.EcardName);
        formData.append('Description', values.Description || '');
        formData.append('CelebrationCategoryId', values.CelebrationCategoryId);
        formData.append('Image', image, image.name);

        // Log formData entries for debugging
        Array.from(formData.entries()).forEach(([key, value]) => {
          console.log(`${key}: ${value}`);
        });

        const token = await fetchToken();
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/CelebrationEcards`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      message.success('Ecard(s) uploaded successfully!');
      form.resetFields();
      setImages([]);
      
      // Call callback function if provided
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error('Error uploading ecard:', error);
      message.error('Failed to upload ecard.');
    } finally {
      setLoading(false);
    }
  };

  // Handle multiple file upload
  const handleMultipleUpload = async (file: File) => {
    setImages(prevImages => [...prevImages, file]);
    return false;
  };

  // Remove image from selected images
  const removeImage = (indexToRemove: number) => {
    setImages(prevImages => prevImages.filter((_, index) => index !== indexToRemove));
  };

  // Upload configuration
  const uploadProps = {
    name: 'file',
    multiple: true,
    beforeUpload: handleMultipleUpload,
    onRemove: (file: any) => {
      setImages(prevImages => prevImages.filter(image => image !== file));
    },
    showUploadList: false,
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <Form.Item
          name="EcardName"
          label="Ecard Name"
          rules={[
            { required: true, message: 'Please enter an ecard name' },
            { max: 50, message: 'Ecard name cannot exceed 50 characters' }
          ]}
          style={{ marginBottom: '15px' }}
        >
          <Input placeholder="Enter ecard name" maxLength={50} />
        </Form.Item>
        <Form.Item
          name="CelebrationCategoryId"
          label="Celebration Category"
          rules={[{ required: true, message: 'Please select a celebration category' }]}
          style={{ marginBottom: '15px' }}
        >
          <Select placeholder="Select a celebration category">
            {categories.map(category => (
              <Option key={category.celebrationCategoryId} value={category.celebrationCategoryId}>
                {category.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      
      <Form.Item
        name="Description"
        label="Description"
        style={{ marginBottom: '15px' }}
        rules={[{ required: true, message: 'Please enter a description' },
            { max: 500, message: 'Description cannot exceed 500 characters' }
        ]}
      >
        <Input.TextArea rows={3} placeholder="Enter description" />
      </Form.Item>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'start' }}>
        <Form.Item label="Upload Image" style={{ marginBottom: '15px' }}>
          <Dragger {...uploadProps} style={{ height: '150px' }}>
            <div>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to upload</p>
            </div>
          </Dragger>
        </Form.Item>
        
        <div>
          {images.length > 0 && (
            <div>
              <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                Selected Images ({images.length})
              </label>
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {images.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <img
                      src={URL.createObjectURL(image)}
                      alt={`Selected ${index}`}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                    <span style={{ flex: 1, fontSize: '12px', color: '#666' }}>
                      {image.name}
                    </span>
                    <Button
                      type="text"
                      danger
                      size="small"
                      onClick={() => removeImage(index)}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Form.Item style={{ marginTop: '20px', textAlign: 'center' }}>
        <Button 
          type="primary" 
          htmlType="submit" 
          size="large"
          loading={loading}
        >
          Upload All Ecards
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AddEcardForm;